import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeadlines, useCompleteDeadline, useUpdateDeadline } from '../../hooks/useDeadlines';
import { useAttorney } from '../../context/AttorneyContext';
import './Deadlines.css';

/**
 * Deadlines Component
 * Shows all upcoming deadlines for attorney review
 * Supports ?id={deadlineId} URL param to highlight a specific deadline
 */
function Deadlines() {
  const { t } = useTranslation('attorney');
  const { isAdmin, isInternal, isAttorneyAdmin } = useAttorney();
  const [daysAhead, setDaysAhead] = useState(30);
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [highlightedId, setHighlightedId] = useState(null);
  const highlightedRef = useRef(null);

  // Read URL params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    if (id) {
      setHighlightedId(id);
      setDaysAhead(365); // Show all deadlines to ensure we can find it
    }
  }, []);

  const { data, isLoading, error, refetch } = useDeadlines({ daysAhead, assignmentFilter });
  const completeDeadlineMutation = useCompleteDeadline();
  const updateDeadlineMutation = useUpdateDeadline();

  // Extract data early so we can use it in useEffect
  const { deadlines = [], stats = {} } = data || {};

  // Scroll to highlighted deadline when data loads
  useEffect(() => {
    if (highlightedId && deadlines.length > 0 && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedId, deadlines.length]); // Use .length to avoid re-running on every render

  const handleComplete = async (deadlineId) => {
    try {
      await completeDeadlineMutation.mutateAsync({
        deadlineId,
        data: { notes: completionNotes }
      });
      setSelectedDeadline(null);
      setCompletionNotes('');
    } catch (err) {
      console.error('Failed to complete deadline:', err);
    }
  };

  const handleEditClick = (deadline) => {
    // Format the date for the input (YYYY-MM-DD)
    const date = new Date(deadline.dueDate);
    const formattedDate = date.toISOString().split('T')[0];
    setEditDate(formattedDate);
    setEditingDeadline(deadline);
  };

  const handleSaveDate = async () => {
    if (editingDeadline && editDate) {
      try {
        await updateDeadlineMutation.mutateAsync({
          deadlineId: editingDeadline.id,
          data: { dueDate: new Date(editDate).toISOString() }
        });
        setEditingDeadline(null);
        setEditDate('');
      } catch (err) {
        console.error('Failed to update deadline:', err);
      }
    }
  };

  const getUrgencyClass = (urgency) => {
    switch (urgency) {
      case 'overdue': return 'urgency-overdue';
      case 'critical': return 'urgency-critical';
      case 'high': return 'urgency-high';
      case 'medium': return 'urgency-medium';
      default: return 'urgency-low';
    }
  };

  const getUrgencyLabel = (urgency) => {
    switch (urgency) {
      case 'overdue': return t('attorney.deadlines.urgency.overdue', 'OVERDUE');
      case 'critical': return t('attorney.deadlines.urgency.critical', 'Critical');
      case 'high': return t('attorney.deadlines.urgency.high', 'High');
      case 'medium': return t('attorney.deadlines.urgency.medium', 'Medium');
      default: return t('attorney.deadlines.urgency.low', 'Low');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const days = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    if (days === 1) return '1 day remaining';
    return `${days} days remaining`;
  };

  if (isLoading) {
    return (
      <div className="deadlines-loading">
        <div className="loading-spinner" />
        <p>{t('attorney.deadlines.loading', 'Loading deadlines...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="deadlines-error">
        <p>{t('attorney.deadlines.error', 'Failed to load deadlines')}</p>
        <button onClick={() => refetch()}>
          {t('attorney.deadlines.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="deadlines-container">
      <div className="deadlines-header">
        <h1>{t('attorney.deadlines.title', 'Deadlines')}</h1>
        <div className="deadlines-controls">
          {isInternal && (
            <select
              value={assignmentFilter}
              onChange={(e) => setAssignmentFilter(e.target.value)}
              className="assignment-filter-select"
            >
              <option value="all">
                {t('attorney.deadlines.allAssignments', 'All Assignments')}
              </option>
              <option value="mine">
                {t('attorney.deadlines.assignedToMe', 'Assigned to Me')}
              </option>
              <option value="my_firm">
                {t('attorney.deadlines.myFirm', 'My Firm')}
              </option>
              {isAttorneyAdmin && (
                <option value="transferred">
                  {t('attorney.deadlines.transferred', 'Transferred to External')}
                </option>
              )}
            </select>
          )}
          <select
            value={daysAhead}
            onChange={(e) => setDaysAhead(Number(e.target.value))}
            className="days-ahead-select"
          >
            <option value={7}>{t('attorney.deadlines.next7days', 'Next 7 days')}</option>
            <option value={14}>{t('attorney.deadlines.next14days', 'Next 14 days')}</option>
            <option value={30}>{t('attorney.deadlines.next30days', 'Next 30 days')}</option>
            <option value={60}>{t('attorney.deadlines.next60days', 'Next 60 days')}</option>
            <option value={90}>{t('attorney.deadlines.next90days', 'Next 90 days')}</option>
            <option value={365}>{t('attorney.deadlines.next365days', 'Next 365 days')}</option>
            <option value={9999}>{t('attorney.deadlines.all', 'All deadlines')}</option>
          </select>
          <button onClick={() => refetch()} className="refresh-btn">
            {t('attorney.deadlines.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="deadlines-stats">
        <div className="stat-card stat-overdue">
          <span className="stat-value">{stats.overdue || 0}</span>
          <span className="stat-label">{t('attorney.deadlines.stats.overdue', 'Overdue')}</span>
        </div>
        <div className="stat-card stat-critical">
          <span className="stat-value">{stats.critical || 0}</span>
          <span className="stat-label">{t('attorney.deadlines.stats.critical', 'Critical')}</span>
        </div>
        <div className="stat-card stat-high">
          <span className="stat-value">{stats.high || 0}</span>
          <span className="stat-label">{t('attorney.deadlines.stats.high', 'High')}</span>
        </div>
        <div className="stat-card stat-total">
          <span className="stat-value">{stats.total || 0}</span>
          <span className="stat-label">{t('attorney.deadlines.stats.total', 'Total')}</span>
        </div>
      </div>

      {/* Deadlines List */}
      {deadlines.length === 0 ? (
        <div className="deadlines-empty">
          <p>{t('attorney.deadlines.empty', 'No upcoming deadlines')}</p>
        </div>
      ) : (
        <div className="deadlines-list">
          {deadlines.map((deadline) => {
            const isHighlighted = deadline.id === highlightedId;
            return (
            <div
              key={deadline.id}
              ref={isHighlighted ? highlightedRef : null}
              className={`deadline-card ${getUrgencyClass(deadline.urgency)} ${isHighlighted ? 'deadline-highlighted' : ''}`}
            >
              <div className="deadline-urgency-badge">
                {getUrgencyLabel(deadline.urgency)}
              </div>

              <div className="deadline-content">
                <div className="deadline-header">
                  <h3 className="deadline-title">{deadline.title}</h3>
                  <span className="deadline-type">{deadline.type}</span>
                </div>

                <div className="deadline-details">
                  <div className="deadline-date">
                    <span className="deadline-due-date">{formatDate(deadline.dueDate)}</span>
                    <span className="deadline-remaining">{getDaysRemaining(deadline.dueDate)}</span>
                  </div>

                  {deadline.jurisdiction && (
                    <span className="deadline-jurisdiction">{deadline.jurisdiction}</span>
                  )}

                  {/* Assignment info */}
                  {deadline.assignment?.isTransferred && (
                    <span className="deadline-assignment transferred">
                      {deadline.assignment.orgName || 'External Firm'}
                    </span>
                  )}
                </div>

                {deadline.description && (
                  <p className="deadline-description">{deadline.description}</p>
                )}
              </div>

              <div className="deadline-actions">
                {deadline.applicationId && (
                  <button
                    className="btn-view"
                    onClick={() => window.open(`/attorney/filing-queue?app=${deadline.applicationId}`, '_blank')}
                  >
                    {t('attorney.deadlines.viewApplication', 'View Application')}
                  </button>
                )}
                <button
                  className="btn-edit"
                  onClick={() => handleEditClick(deadline)}
                >
                  {t('attorney.deadlines.editDate', 'Edit Date')}
                </button>
                <button
                  className="btn-complete"
                  onClick={() => setSelectedDeadline(deadline)}
                >
                  {t('attorney.deadlines.markComplete', 'Mark Complete')}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Complete Modal */}
      {selectedDeadline && (
        <div className="modal-overlay" onClick={() => setSelectedDeadline(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('attorney.deadlines.completeTitle', 'Complete Deadline')}</h2>
            <p className="modal-deadline-title">{selectedDeadline.title}</p>

            <div className="form-group">
              <label>{t('attorney.deadlines.completionNotes', 'Completion Notes')}</label>
              <textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder={t('attorney.deadlines.notesPlaceholder', 'Add any notes about how this deadline was handled...')}
                rows={4}
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setSelectedDeadline(null)}
              >
                {t('attorney.deadlines.cancel', 'Cancel')}
              </button>
              <button
                className="btn-confirm"
                onClick={() => handleComplete(selectedDeadline.id)}
                disabled={completeDeadlineMutation.isPending}
              >
                {completeDeadlineMutation.isPending
                  ? t('attorney.deadlines.completing', 'Completing...')
                  : t('attorney.deadlines.confirm', 'Confirm Complete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Date Modal */}
      {editingDeadline && (
        <div className="modal-overlay" onClick={() => setEditingDeadline(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('attorney.deadlines.editDateTitle', 'Edit Deadline Date')}</h2>
            <p className="modal-deadline-title">{editingDeadline.title}</p>

            <div className="edit-date-warning">
              <span className="warning-icon">⚠️</span>
              <p>
                {t('attorney.deadlines.editWarning',
                  'This deadline was automatically generated. Please verify the correct due date with the official correspondence before saving.'
                )}
              </p>
            </div>

            <div className="form-group">
              <label>{t('attorney.deadlines.newDueDate', 'New Due Date')}</label>
              <input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="date-input"
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setEditingDeadline(null)}
              >
                {t('attorney.deadlines.cancel', 'Cancel')}
              </button>
              <button
                className="btn-confirm"
                onClick={handleSaveDate}
                disabled={!editDate || updateDeadlineMutation.isPending}
              >
                {updateDeadlineMutation.isPending
                  ? t('attorney.deadlines.saving', 'Saving...')
                  : t('attorney.deadlines.saveDate', 'Save Date')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Deadlines;
